import React from 'react';
import {connect} from 'react-redux';

import {assert} from 'Asserts';
import * as SampleConstants from 'constants/SampleConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BaseParentStorableDataSelector} from 'samples/ParentStorableDataSelector';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {isGridId} from 'samples/SampleUtil';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

import {goToPrev, removeParent, selectParent, uploadFromPlateMap} from './UploadContainersReduxActions';

interface ConnectedProps {
  helpText: string;
  isUploading: boolean;
  numOrphans: number;
  selectedParent: SampleConstants.SelectedParentStorable;
  storableSchema: StorableSchema | null;
}

function mapStateToProps(state: BenchlingState): ConnectedProps {
  const {changeStats, gridPreview, isUploading, selectedContainerSchemaId, selectedParent} =
    state.ui.samples.uploadContainersFlow;
  assert(changeStats != null, 'changeStats should be set before rendering SelectOrphanParentStorableModal');
  const numOrphans = changeStats.moveOut;
  return {
    helpText: `${numOrphans} containers will be moved out of ${
      gridPreview!.barcode
    }. Select their new location`,
    isUploading,
    numOrphans,
    selectedParent,
    storableSchema: SampleSchemaStore.getContainerSchema(selectedContainerSchemaId),
  };
}

interface BaseSelectOrphanParentStorableModalProps {
  onRequestClose: () => void;
  title: string;
}

type SelectOrphanParentStorableModalProps = BaseSelectOrphanParentStorableModalProps &
  ConnectedProps &
  DispatchProps;

export class SelectOrphanParentStorableModal extends React.Component<SelectOrphanParentStorableModalProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  _shouldDisableSave(): boolean {
    const {isUploading, numOrphans, selectedParent} = this.props;
    const {gridCells, storable} = selectedParent;
    if (!storable || isUploading) {
      return true;
    }
    return isGridId(storable.id) && gridCells.length !== numOrphans;
  }

  override render(): JSX.Element {
    const {
      helpText,
      isUploading,
      onGoToPrevious,
      onRequestClose,
      onLoadGrid,
      onRemove,
      onSelect,
      onSubmit,
      selectedParent,
      storableSchema,
      title,
    } = this.props;

    return (
      <Modal
        buttons={[
          {
            pullLeft: true,
            style: ButtonStyle.TERTIARY_GRAY,
            text: 'Back',
            onClick: onGoToPrevious,
          },
          {
            text: isUploading ? 'Uploading...' : 'Submit',
            disabled: this._shouldDisableSave(),
            submit: true,
            style: ButtonStyle.PRIMARY,
          },
        ]}
        closeableOnEscapeKey={false}
        onRequestClose={onRequestClose}
        onRequestSubmit={onSubmit}
        title={title}
      >
        <div className="u-small-spacing-bottom">{helpText}</div>
        <BaseParentStorableDataSelector
          autoFocus={false}
          canSelectMultipleGridCells={true}
          onLoadGrid={onLoadGrid}
          onRemove={onRemove}
          onSelect={onSelect}
          isRequired={true}
          selectedParent={selectedParent}
          storableSchema={storableSchema}
        />
      </Modal>
    );
  }
}

interface DispatchProps {
  onGoToPrevious: typeof goToPrev;
  onLoadGrid: typeof selectParent;
  onRemove: typeof removeParent;
  onSelect: typeof selectParent;
  onSubmit: typeof uploadFromPlateMap;
}

export default connect(mapStateToProps, {
  onGoToPrevious: goToPrev,
  onLoadGrid: selectParent,
  onRemove: removeParent,
  onSelect: selectParent,
  onSubmit: uploadFromPlateMap,
})(SelectOrphanParentStorableModal);
